import { useCallback, useEffect, useRef, useState } from 'react';
import { Client } from '@stomp/stompjs';
import SockJS from 'sockjs-client';
import apiService from '../services/apiService.ts';

const REFRESH_INTERVAL_MS = 5000;
const RESERVATION_EVENT_PREFIX = 'TABLE_RESERVATION_';

const toTimestamp = (value: unknown) => {
  if (typeof value !== 'string' || value.length === 0) {
    return 0;
  }

  const parsed = Date.parse(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const normalizeRows = (value: unknown) => {
  if (!Array.isArray(value)) {
    return [];
  }

  return [...value].sort((left: any, right: any) => {
    const leftTime = toTimestamp(left?.requestedTime ?? left?.createdAt);
    const rightTime = toTimestamp(right?.requestedTime ?? right?.createdAt);
    return rightTime - leftTime;
  });
};

export const useReservations = (tenantId:string, from?:string, to?:string) => {
  const [rows,setRows]=useState<any[]>([]);
  const [loading,setLoading]=useState(false);
  const hasLoadedRef = useRef(false);

  const fetchData = useCallback(async (background = false)=>{
    if(!tenantId) {
      setRows([]);
      hasLoadedRef.current = false;
      setLoading(false);
      return;
    }

    const shouldShowLoading = !background || !hasLoadedRef.current;
    if (shouldShowLoading) {
      setLoading(true);
    }

    try {
      const reservations = await apiService.getReservations(tenantId,from,to);
      setRows(normalizeRows(reservations));
      hasLoadedRef.current = true;
    }
    finally {
      if (shouldShowLoading) {
        setLoading(false);
      }
    }  
  },[tenantId,from,to]);

  useEffect(()=>{
    hasLoadedRef.current = false;
    void fetchData();
  },[fetchData]);

  useEffect(() => {
    if (!tenantId) {
      return undefined;
    }

    const intervalId = window.setInterval(() => {
      void fetchData(true);
    }, REFRESH_INTERVAL_MS);

    const handleFocus = () => {
      void fetchData(true);
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        void fetchData(true);
      }
    };

    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [fetchData, tenantId]);

  useEffect(() => {
    const token = localStorage.getItem('accessToken');
    if (!tenantId || !token) {
      return undefined;
    }

    const client = new Client({
      webSocketFactory: () => new SockJS('/ws'),
      connectHeaders: {
        Authorization: `Bearer ${token}`,
      },
      reconnectDelay: 5000,
      debug: () => {},
      onConnect: () => {
        client.subscribe(`/topic/tenants/${tenantId}/notifications`, (message) => {
          try {
            const payload = JSON.parse(message.body) as { type?: string };
            if (payload?.type?.startsWith(RESERVATION_EVENT_PREFIX)) {
              void fetchData(true);
            }
          } catch {
            void fetchData(true);
          }
        });
      },
    });

    client.activate();
    return () => {
      client.deactivate();
    };
  }, [fetchData, tenantId]);

  return { rows, loading, refetch: fetchData };
};
